// store/Slices/withdrawSlice.js
import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import api from "../../API/api";

const BASE = "/withdrawals";

/* ============ Thunks ============ */
export const feeQuote = createAsyncThunk(
  "withdraw/feeQuote",
  async ({ asset_symbol, amount }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${BASE}/fee-quote`, {
        params: { asset_symbol, amount },
      });
      return data;
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to fetch quote";
      return rejectWithValue(msg);
    }
  }
);

export const createWithdrawal = createAsyncThunk(
  "withdraw/create",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`${BASE}`, payload);
      return data.request;
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to create withdrawal";
      return rejectWithValue(msg);
    }
  }
);

export const fetchMyWithdrawals = createAsyncThunk(
  "withdraw/fetchMine",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${BASE}/mine`);
      return Array.isArray(data?.requests) ? data.requests : [];
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to fetch withdrawals";
      return rejectWithValue(msg);
    }
  }
);

export const fetchAllWithdrawals = createAsyncThunk(
  "withdraw/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${BASE}`);
      return Array.isArray(data?.requests) ? data.requests : [];
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to fetch all withdrawals";
      return rejectWithValue(msg);
    }
  }
);

export const approveWithdrawal = createAsyncThunk(
  "withdraw/approve",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`${BASE}/${id}/approve`);
      return data.request;
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to approve withdrawal";
      return rejectWithValue(msg);
    }
  }
);

export const rejectWithdrawal = createAsyncThunk(
  "withdraw/reject",
  async ({ id, reason }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`${BASE}/${id}/reject`, { reason });
      return data.request;
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to reject withdrawal";
      return rejectWithValue(msg);
    }
  }
);

export const completeWithdrawal = createAsyncThunk(
  "withdraw/complete",
  async ({ id }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`${BASE}/${id}/complete`);
      return data.request;
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to complete withdrawal";
      return rejectWithValue(msg);
    }
  }
);

/* ============ Slice ============ */
const initialState = {
  mine: [],
  all: [],
  quote: null,
  listLoading: false,
  actionLoading: false,
  quoteLoading: false,
  error: null,
  lastUpdatedAt: null,
};

const slice = createSlice({
  name: "withdraw",
  initialState,
  reducers: {
    wsUpdateStatus: (state, action) => {
      const { id, status } = action.payload || {};
      const idStr = String(id || "");
      const update = (arr) => {
        const idx = arr.findIndex((r) => String(r?._id) === idStr);
        if (idx > -1) {
          arr[idx] = {
            ...arr[idx],
            status: status ?? arr[idx].status,
            updated_at: new Date().toISOString(),
          };
        }
      };
      update(state.mine);
      update(state.all);
      state.lastUpdatedAt = Date.now();
    },
    wsNewRequest: (state, action) => {
      const req = action.payload;
      if (req && req._id) {
        const exists = state.all.some((r) => String(r._id) === String(req._id));
        if (!exists) state.all.unshift(req);
      }
      state.lastUpdatedAt = Date.now();
    },
    clearWithdrawError: (state) => { state.error = null; },
    clearQuote: (state) => { state.quote = null; },
  },
  extraReducers: (builder) => {
    builder
      // feeQuote
      .addCase(feeQuote.pending, (s) => { s.quoteLoading = true; s.error = null; })
      .addCase(feeQuote.fulfilled, (s, a) => { s.quoteLoading = false; s.quote = a.payload; })
      .addCase(feeQuote.rejected, (s, a) => { s.quoteLoading = false; s.error = a.payload; })

      // create
      .addCase(createWithdrawal.pending, (s) => { s.actionLoading = true; s.error = null; })
      .addCase(createWithdrawal.fulfilled, (s, a) => { s.actionLoading = false; if (a.payload?._id) s.mine = [a.payload, ...s.mine]; s.lastUpdatedAt = Date.now(); s.quote = null; })
      .addCase(createWithdrawal.rejected, (s, a) => { s.actionLoading = false; s.error = a.payload; })

      // mine
      .addCase(fetchMyWithdrawals.pending, (s) => { s.listLoading = true; s.error = null; })
      .addCase(fetchMyWithdrawals.fulfilled, (s, a) => { s.listLoading = false; s.mine = a.payload || []; s.lastUpdatedAt = Date.now(); })
      .addCase(fetchMyWithdrawals.rejected, (s, a) => { s.listLoading = false; s.error = a.payload; })

      // all
      .addCase(fetchAllWithdrawals.pending, (s) => { s.listLoading = true; s.error = null; })
      .addCase(fetchAllWithdrawals.fulfilled, (s, a) => { s.listLoading = false; s.all = a.payload || []; s.lastUpdatedAt = Date.now(); })
      .addCase(fetchAllWithdrawals.rejected, (s, a) => { s.listLoading = false; s.error = a.payload; });

    const replaceInAll = (s, updated) => {
      if (!updated?._id) return;
      const idx = s.all.findIndex((x) => String(x._id) === String(updated._id));
      if (idx >= 0) s.all[idx] = updated; else s.all.unshift(updated);
      s.lastUpdatedAt = Date.now();
    };

    [approveWithdrawal, rejectWithdrawal, completeWithdrawal].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (s) => { s.actionLoading = true; s.error = null; })
        .addCase(thunk.fulfilled, (s, a) => { s.actionLoading = false; replaceInAll(s, a.payload); })
        .addCase(thunk.rejected, (s, a) => { s.actionLoading = false; s.error = a.payload; });
    });
  },
});

export const { wsUpdateStatus, wsNewRequest, clearWithdrawError, clearQuote } = slice.actions;
export default slice.reducer;

// Selectors
const selectWithdraw = (state) => state.withdraw || initialState;
export const selectMineWithdrawals   = createSelector([selectWithdraw], (s) => s.mine);
export const selectAllWithdrawals    = createSelector([selectWithdraw], (s) => s.all);
export const selectWithdrawLoading   = createSelector([selectWithdraw], (s) => s.listLoading || s.actionLoading);
export const selectWithdrawError     = createSelector([selectWithdraw], (s) => s.error);
export const selectWithdrawUpdatedAt = createSelector([selectWithdraw], (s) => s.lastUpdatedAt);
export const selectQuote             = createSelector([selectWithdraw], (s) => s.quote);
export const selectQuoteLoading      = createSelector([selectWithdraw], (s) => s.quoteLoading);
